/**
 * Guide Experiences Page
 *
 * Page for guides to manage their outdoor experiences and offerings.
 *
 * Epic: MVP 1.12 - Guide Management Dashboard & Analytics
 *
 * @version 1.1
 * @created 2025-11-23
 * @updated 2025-12-06 - Added experience listing with API integration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import EmptyState from '../../components/common/EmptyState';
import ExperienceCard from '../../components/experiences/ExperienceCard';
import { apiRequest } from '../../services/api';
import { deriveExperienceStatus } from '../../utils/experienceStatus';

export default function GuideExperiences() {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // SCRUM-1725: Calculate stats using shared deriveExperienceStatus helper
  // Summary counts and card badges both use the same derivation — no disagreement possible.
  const stats = {
    total: experiences.length,
    published: experiences.filter(exp => deriveExperienceStatus(exp) === 'published').length,
    drafts: experiences.filter(exp => deriveExperienceStatus(exp) === 'draft').length,
    inactive: experiences.filter(exp => deriveExperienceStatus(exp) === 'inactive').length,
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/v1/guides/me/experiences');

      // Transform API data to match ExperienceCard format
      const transformedExperiences = (response.data?.experiences || []).map(exp => ({
        ...exp,
        // Add required properties for ExperienceCard
        images: exp.images || [
          'https://images.unsplash.com/photo-1509233725247-49e657c54213?w=800',
          'https://images.unsplash.com/photo-1445077100181-a33e9ac94db0?w=800'
        ], // Default images if none provided
        location: exp.meeting_location_name || exp.meeting_state || 'Location',
        rating: exp.avg_rating || 0,
        reviews: exp.total_reviews || 0,
        // ARCH-CONV-001: base_price_cents from spread; ExperienceCard formats at display boundary
        duration: exp.duration_type === 'fixed_multi_day' && exp.duration_days
          ? `${exp.duration_days}-Day Trip`
          : exp.duration_hours ? `${exp.duration_hours} hours` : exp.duration || '',
      }));

      setExperiences(transformedExperiences);
    } catch (err) {
      console.error('Failed to fetch experiences:', err);
      setError(err.message || 'Failed to load experiences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="experiences"
      onNavigate={(id, path) => path && navigate(path)}
      user={{
        name: 'Guide User',
        email: 'guide@outdoorguides.com',
        avatar: null
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* Page Header */}
        <div>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)'
          }}>
            My Experiences
          </h1>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            Manage your outdoor experiences and offerings
          </p>
        </div>

        {/* Stats Summary — SCRUM-1725: 4-cell layout (Total / Published / Drafts / Inactive) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--space-4)'
        }}>
          <div data-testid="summary-cell" style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              Total Experiences
            </div>
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)', marginTop: 'var(--space-1)' }}>
              {stats.total}
            </div>
          </div>
          <div data-testid="summary-cell" style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              Published
            </div>
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)', marginTop: 'var(--space-1)', color: 'var(--color-success)' }}>
              {stats.published}
            </div>
          </div>
          <div data-testid="summary-cell" style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              Drafts
            </div>
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)', marginTop: 'var(--space-1)', color: '#eab308' }}>
              {stats.drafts}
            </div>
          </div>
          <div data-testid="summary-cell" style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              Inactive
            </div>
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)', marginTop: 'var(--space-1)', color: 'var(--color-text-secondary)' }}>
              {stats.inactive}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <LoadingSpinner message="Loading experiences..." />
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert variant="error" message={error} />
        )}

        {/* Empty State */}
        {!loading && !error && experiences.length === 0 && (
          <EmptyState
            icon={Plus}
            title="Create Your First Experience"
            description="Get started by creating your first outdoor experience to accept bookings"
            action={
              <button
                onClick={() => navigate('/guide/experiences/new')}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-semibold text-sm transition-colors"
              >
                Create Experience
              </button>
            }
          />
        )}

        {/* Experiences Grid */}
        {!loading && !error && experiences.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 'var(--space-6)'
          }}>
            {experiences.map((experience) => (
              <ExperienceCard
                key={experience.id}
                experience={experience}
                variant="guideProfile"
              />
            ))}

            {/* Add New Experience Card */}
            <button
              onClick={() => navigate('/guide/experiences/new')}
              style={{
                padding: 'var(--space-8)',
                backgroundColor: 'var(--color-background)',
                border: '2px dashed var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.backgroundColor = 'var(--color-background)';
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-background-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'var(--space-4)'
              }}>
                <Plus size={32} style={{ color: 'var(--color-text-tertiary)' }} />
              </div>
              <h3 style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Create New Experience
              </h3>
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                textAlign: 'center'
              }}>
                Add a new outdoor experience to your offerings
              </p>
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
